
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import LogoutIcon from "@mui/icons-material/Logout";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import Navbar from "../components/Homepage/Navbar/Navbar";
import Footer from "../components/Homepage/Footer/Footer";

const API_URL = "https://rukap.edu.in/leave-api";
// const API_URL = "http://127.0.0.1:5000";

const navy = "#17365D";

const formatDate = (dateValue) => {
  if (!dateValue) return "-";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return String(dateValue);
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
};

const formatDateTime = () => {
  return new Date().toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Kolkata",
    timeZoneName: "short",
  });
};

const getStatusColor = (status) => {
  switch (String(status || "").toUpperCase()) {
    case "APPROVED":
      return "#237447";
    case "PENDING":
    case "SUBMITTED":
      return "#946200";
    case "REJECTED":
      return "#B42318";
    default:
      return "#444444";
  }
};

const EmployeeLeaveDetails = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();

  const [employee, setEmployee] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const token = sessionStorage.getItem("leaveToken");
  const storedUser = sessionStorage.getItem("leaveUser");

  let currentUser = null;

  try {
    currentUser = storedUser ? JSON.parse(storedUser) : null;
  } catch {
    currentUser = null;
  }

  const role = String(currentUser?.role || "").toUpperCase();

  useEffect(() => {
    let cancelled = false;

    const loadDetails = async () => {
      if (!token || !currentUser) {
        navigate("/leave-portal");
        return;
      }

      const allowedRoles = [
        "HOD",
        "PRINCIPAL",
        "REGISTRAR",
        "ADMIN",
      ];

      if (!allowedRoles.includes(role)) {
        setError("You are not authorized to view employee details.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const headers = {
          Authorization: `Bearer ${token}`,
        };

        const [employeeResponse, leaveResponse] = await Promise.all([
          axios.get(`${API_URL}/api/admin/employees`, { headers }),
          axios.get(`${API_URL}/api/admin/leaves`, { headers }),
        ]);

        if (cancelled) return;

        const employeeData =
          employeeResponse.data?.employees ||
          employeeResponse.data?.data ||
          [];

        const leaveData =
          leaveResponse.data?.applications ||
          leaveResponse.data?.data ||
          [];

        const selectedEmployee = employeeData.find(
          (item) =>
            String(item.employee_id) === String(employeeId)
        );

        if (!selectedEmployee) {
          setError(
            "Employee not found or you do not have permission to view this employee."
          );
          setEmployee(null);
          setApplications([]);
          return;
        }

        const employeeApplications = leaveData
          .filter(
            (item) =>
              String(item.employee_id) === String(employeeId)
          )
          .map((item) => ({
            ...item,
            dates: Array.isArray(item.dates)
              ? item.dates
              : typeof item.leave_dates === "string"
              ? item.leave_dates.split(",").filter(Boolean)
              : [],
          }))
          .sort(
            (a, b) =>
              new Date(b.submitted_at || 0) -
              new Date(a.submitted_at || 0)
          );

        setEmployee(selectedEmployee);
        setApplications(employeeApplications);
      } catch (err) {
        if (cancelled) return;

        console.error("Error loading employee details:", err);

        setError(
          err.response?.data?.message ||
            "Unable to load employee details. Please check the server and try again."
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadDetails();

    return () => {
      cancelled = true;
    };
  }, [employeeId, token, role, navigate]);

  const entitlement = Number(
    employee?.annual_leave_entitlement ?? 0
  );

  const taken = Number(employee?.leave_taken ?? 0);

  const available = Math.max(
    0,
    Number(
      employee?.available_leave ?? entitlement - taken
    )
  );

  const employeeName = employee?.employee_name || "Employee";

  const getApplicationDates = (application) => {
    const dates = Array.isArray(application.dates)
      ? application.dates
      : [];

    return dates.map(formatDate).join(", ") || "-";
  };

  const downloadEmployeePDF = () => {
    if (!employee) return;

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const safeName = employeeName
      .replace(/[^a-zA-Z0-9_-]/g, "_")
      .slice(0, 60);

    // University heading
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(23, 54, 93);

    doc.text(
      "RAYALASEEMA UNIVERSITY",
      pageWidth / 2,
      15,
      { align: "center" }
    );

    doc.setFontSize(11);
    doc.setTextColor(50, 50, 50);

    doc.text(
      "EMPLOYEE LEAVE DETAILS REPORT",
      pageWidth / 2,
      22,
      { align: "center" }
    );

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);

    doc.text(
      `Generated: ${formatDateTime()}`,
      pageWidth - 14,
      29,
      { align: "right" }
    );

    // Employee profile
    autoTable(doc, {
      startY: 34,
      head: [["EMPLOYEE PROFILE", "DETAILS"]],
      body: [
        ["Employee Code", employee.employee_code || "-"],
        ["Employee Name", employeeName],
        ["Employee Type", employee.employee_type || "-"],
        ["Designation", employee.designation || "-"],
        ["Department", employee.department_name || "-"],
        ["College", employee.college_name || "-"],
        ["Mobile", employee.mobile || "-"],
        ["Email", employee.email || "-"],
        ["Leave Year", String(employee.leave_year || "-")],
      ],
      theme: "grid",
      styles: {
        fontSize: 9,
        cellPadding: 2.5,
        textColor: [40, 40, 40],
      },
      headStyles: {
        fillColor: [23, 54, 93],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: 55, fontStyle: "bold" },
        1: { cellWidth: 120 },
      },
    });

    // Leave balance
    const balanceY = doc.lastAutoTable.finalY + 8;

    autoTable(doc, {
      startY: balanceY,
      head: [[
        "ANNUAL ENTITLEMENT (DAYS)",
        "LEAVE TAKEN (DAYS)",
        "AVAILABLE LEAVE (DAYS)",
      ]],
      body: [[entitlement, taken, available]],
      theme: "grid",
      styles: {
        fontSize: 9,
        cellPadding: 3,
        halign: "center",
        textColor: [40, 40, 40],
      },
      headStyles: {
        fillColor: [23, 54, 93],
        textColor: [255, 255, 255],
        halign: "center",
      },
    });

    // Application history
    const historyY = doc.lastAutoTable.finalY + 8;

    const historyRows = applications.map((application) => [
      application.application_no || "-",
      application.leave_type || "-",
      getApplicationDates(application),
      application.total_days ?? "-",
      application.purpose || "-",
      application.status || "-",
      formatDate(application.submitted_at),
    ]);

    autoTable(doc, {
      startY: historyY,
      head: [[
        "Application No.",
        "Leave Type",
        "Leave Dates",
        "Days",
        "Purpose",
        "Status",
        "Submitted Date",
      ]],
      body: historyRows.length
        ? historyRows
        : [["No applications found", "", "", "", "", "", ""]],
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 2.5,
        overflow: "linebreak",
        valign: "middle",
        textColor: [40, 40, 40],
      },
      headStyles: {
        fillColor: [23, 54, 93],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: 32 },
        1: { cellWidth: 22 },
        2: { cellWidth: 48 },
        3: { cellWidth: 12, halign: "center" },
        4: { cellWidth: 65 },
        5: { cellWidth: 25 },
        6: { cellWidth: 30 },
      },
      didDrawPage: () => {
        const pageHeight = doc.internal.pageSize.getHeight();

        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);

        doc.text(
          "Rayalaseema University - Employee Leave Report",
          14,
          pageHeight - 7
        );

        doc.text(
          `Page ${doc.internal.getNumberOfPages()}`,
          pageWidth - 14,
          pageHeight - 7,
          { align: "right" }
        );
      },
    });

    doc.save(`Employee_Leave_Report_${safeName}.pdf`);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("leaveToken");
    sessionStorage.removeItem("leaveUser");
    navigate("/leave-portal");
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <Box
          sx={{
            minHeight: "60vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error || !employee) {
    return (
      <>
        <Navbar />

        <Box sx={{ maxWidth: 1000, mx: "auto", p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || "Employee details are unavailable."}
          </Alert>

          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mr: 1 }}
          >
            Back
          </Button>

          <Button
            variant="contained"
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            sx={{ bgcolor: navy }}
          >
            Logout
          </Button>
        </Box>
      </>
    );
  }

  return (
    <>
      <Navbar />

      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 2, md: 3 },
          py: 3,
          color: "#222",
        }}
      >
        {/* Page heading */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexWrap: "wrap",
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{ color: navy, fontWeight: 600 }}
            >
              Employee Leave Details
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Rayalaseema University
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>

            <Button
              variant="contained"
              startIcon={<PictureAsPdfIcon />}
              onClick={downloadEmployeePDF}
              sx={{
                bgcolor: navy,
                "&:hover": { bgcolor: "#102746" },
              }}
            >
              Download PDF
            </Button>

            <Button
              variant="outlined"
              color="inherit"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
            >
              Logout
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Employee profile */}
        <Paper variant="outlined" sx={{ mb: 3 }}>
          <Box sx={{ px: 2, py: 1.5, bgcolor: "#F5F7FA" }}>
            <Typography fontWeight={600} color={navy}>
              Employee Profile
            </Typography>
          </Box>

          <Divider />

          <Box sx={{ p: 2 }}>
            <Table size="small">
              <TableBody>
                {[
                  ["Employee Code", employee.employee_code],
                  ["Employee Name", employee.employee_name],
                  ["Employee Type", employee.employee_type],
                  ["Designation", employee.designation],
                  ["Department", employee.department_name],
                  ["College", employee.college_name],
                  ["Mobile", employee.mobile],
                  ["Email", employee.email],
                  ["Leave Year", employee.leave_year],
                ].map(([label, value]) => (
                  <TableRow key={label}>
                    <TableCell
                      sx={{
                        width: { xs: "42%", md: "30%" },
                        fontWeight: 600,
                        color: "#333",
                      }}
                    >
                      {label}
                    </TableCell>

                    <TableCell sx={{ overflowWrap: "anywhere" }}>
                      {value === null ||
                      value === undefined ||
                      value === ""
                        ? "-"
                        : String(value)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>

        {/* Leave balance */}
        <Paper variant="outlined" sx={{ mb: 3 }}>
          <Box sx={{ px: 2, py: 1.5, bgcolor: "#F5F7FA" }}>
            <Typography fontWeight={600} color={navy}>
              Leave Balance
            </Typography>
          </Box>

          <Divider />

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>
                    Annual Entitlement
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    Leave Taken
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    Available Leave
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                <TableRow>
                  <TableCell>{entitlement} days</TableCell>
                  <TableCell>{taken} days</TableCell>
                  <TableCell>
                    <Typography
                      component="span"
                      sx={{ color: navy, fontWeight: 700 }}
                    >
                      {available} days
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Application history */}
        <Paper variant="outlined">
          <Box
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: "#F5F7FA",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography fontWeight={600} color={navy}>
              Leave Application History
            </Typography>

            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={downloadEmployeePDF}
            >
              Download PDF
            </Button>
          </Box>

          <Divider />

          {applications.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography color="text.secondary" align="center">
                No leave applications found for this employee.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#FAFAFA" }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Application No.
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Leave Type
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Leave Dates
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Days
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Purpose
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Submitted
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {applications.map((application) => (
                    <TableRow
                      key={application.leave_id}
                      hover
                    >
                      <TableCell>
                        {application.application_no || "-"}
                      </TableCell>

                      <TableCell>
                        {application.leave_type || "-"}
                      </TableCell>

                      <TableCell sx={{ minWidth: 160 }}>
                        {getApplicationDates(application)}
                      </TableCell>

                      <TableCell>
                        {application.total_days ?? "-"}
                      </TableCell>

                      <TableCell sx={{ minWidth: 160 }}>
                        {application.purpose || "-"}
                      </TableCell>

                      <TableCell>
                        <Typography
                          component="span"
                          sx={{
                            color: getStatusColor(application.status),
                            fontWeight: 600,
                          }}
                        >
                          {application.status || "-"}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ minWidth: 100 }}>
                        {formatDate(application.submitted_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 2, textAlign: "center" }}
        >
          Rayalaseema University | Employee Leave Management System
        </Typography>
      </Box>
      <Footer />
    </>
  );
};

export default EmployeeLeaveDetails;

